import clientAPICall from '@/app/components/search/common/clientAPICall.ts';
import { API_CALL_PREFIX } from '@/app/components/search/utils/api/apiUtils.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import type { EventsPayload } from '@/app/api/events/route.ts';
import type { StarTrackEventData } from '@/app/common/starTrack/startrackTypes.ts';
import type { Environment } from '@/domainLogic/index.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';

export const DEFAULT_FLUSH_INTERVAL_MS = 5_000;
export const SEND_IMMEDIATE_TIMEOUT_MS = 2_000;

type BatcherContext = {
  environment: Environment;
  traceParent: string;
  commonData: CommonData;
};

export type StarTrackBatcherConfig = BatcherContext & {
  flushIntervalMs?: number;
};

export type StarTrackEventBatcher = {
  /** Queue events to be sent on the next flush. */
  enqueue: <T>(events: StarTrackEventData<T>[]) => void;
  /**
   * Send events immediately, bypassing the queue, via a fetch raced against a
   * 2 s timeout. Resolves only once the request has actually been sent (or timed
   * out), so a caller that awaits it before calling window.stop() / navigating is
   * guaranteed the event reached the server first.
   */
  sendImmediate: <T>(events: StarTrackEventData<T>[]) => Promise<void>;
  /** Send any queued events now. */
  flush: () => void;
  /** Update the environment / traceParent used for subsequent flushes. */
  reconfigure: (context: BatcherContext) => void;
  /** Stop the timer, detach listeners and flush remaining events. */
  dispose: () => void;
};

/**
 * Collects StarTrack events and sends them to `/api/events` in a single batched
 * call every `flushIntervalMs` (default 10s) instead of one call per event.
 *
 * The `/api/events` route already accepts an array of events, so batching is a
 * purely client-side concern.
 *
 * Queued events are also flushed when the page is hidden / unloaded (via
 * `sendBeacon`, which survives navigation) so that events still sitting in the
 * queue when the user leaves the page are not lost.
 */
export const createStarTrackEventBatcher = ({
  environment,
  traceParent,
  commonData,
  flushIntervalMs = DEFAULT_FLUSH_INTERVAL_MS,
}: StarTrackBatcherConfig): StarTrackEventBatcher => {
  let queue: StarTrackEventData<unknown>[] = [];
  let currentEnvironment = environment;
  let currentTraceParent = traceParent;
  let currentCommonData = commonData;

  const sendViaBeacon = (payload: EventsPayload<unknown>): boolean => {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
    const body = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    return navigator.sendBeacon(`${API_CALL_PREFIX}${API_NAME.EVENTS}`, body);
  };

  // `useBeacon` is used on page unload, where an async fetch may be cancelled.
  const flush = (useBeacon = false): void => {
    if (queue.length === 0) return;
    const starTrackEventData = queue;
    queue = [];
    const payload: EventsPayload<unknown> = { starTrackEventData, environment: currentEnvironment };
    try {
      if (useBeacon && sendViaBeacon(payload)) return;
      // Fire and forget: clientAPICall swallows its own errors internally.
      clientAPICall<EventsPayload<unknown>, undefined>({
        apiName: API_NAME.EVENTS,
        payload,
        traceParent: currentTraceParent,
        commonData: currentCommonData,
      });
    } catch (e) {
      console.log(e);
    }
  };

  const enqueue = <T>(events: StarTrackEventData<T>[]): void => {
    queue.push(...(events as StarTrackEventData<unknown>[]));
  };

  // Does not touch the queue — events are sent directly so that the returned
  // promise genuinely resolves when the send is settled (or times out).
  //
  // We intentionally use fetch here rather than navigator.sendBeacon: the caller
  // (useBookRoom.redirectToCheckout) awaits this promise and then calls
  // window.stop() before navigating. sendBeacon only *queues* the request and
  // returns synchronously, so window.stop() cancels it before it is transmitted
  // and CLICK2CHECKOUT is lost. Awaiting a real fetch guarantees the event has
  // reached the server first; the wait is capped so checkout is never blocked
  // indefinitely on a slow network.
  const sendImmediate = async <T>(events: StarTrackEventData<T>[]): Promise<void> => {
    const payload: EventsPayload<unknown> = {
      starTrackEventData: events as StarTrackEventData<unknown>[],
      environment: currentEnvironment,
    };
    await Promise.race([
      clientAPICall<EventsPayload<unknown>, undefined>({
        apiName: API_NAME.EVENTS,
        payload,
        traceParent: currentTraceParent,
        commonData: currentCommonData,
      }).catch(() => {}),
      new Promise<void>((resolve) => {
        setTimeout(resolve, SEND_IMMEDIATE_TIMEOUT_MS);
      }),
    ]);
  };

  const reconfigure = ({ environment: env, traceParent: tp, commonData: cd }: BatcherContext): void => {
    currentEnvironment = env;
    currentTraceParent = tp;
    currentCommonData = cd;
  };

  let intervalId: ReturnType<typeof setInterval> | undefined;
  const handlePageHide = (): void => flush(true);
  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') flush(true);
  };

  if (typeof window !== 'undefined') {
    intervalId = setInterval(() => flush(), flushIntervalMs);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  const dispose = (): void => {
    if (intervalId !== undefined) clearInterval(intervalId);
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    flush();
  };

  return { enqueue, sendImmediate, flush: () => flush(), reconfigure, dispose };
};
